import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Briefcase, Upload, BarChart3, Settings, Tag,
  CheckSquare, UserCog, LogOut, Search, Bell, Handshake, KeyRound, ChevronDown, User as UserIcon, Target, PieChart, Megaphone, UserCheck,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "full_access", "restricted"] },
  { to: "/leads", label: "Leads", icon: Users, roles: ["admin", "full_access", "restricted"] },
  { to: "/opportunities", label: "Opportunities", icon: Briefcase, roles: ["admin", "full_access", "restricted"] },
  { to: "/partnership-pipeline", label: "Partnerships Pipeline", icon: Handshake, roles: ["admin", "full_access", "restricted"] },
  { to: "/clients", label: "Clients", icon: UserCheck, roles: ["admin", "full_access"] },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, roles: ["admin", "full_access", "restricted"] },
  { to: "/pipeline-report", label: "Pipeline Report", icon: PieChart, roles: ["admin", "full_access", "restricted"] },
  { to: "/import", label: "Import Leads", icon: Upload, roles: ["admin"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "full_access"] },
  { to: "/team", label: "Team Members", icon: UserCog, roles: ["admin"] },
  { to: "/goals", label: "Team Goals", icon: Target, roles: ["admin"] },
  { to: "/partnerships", label: "Partnerships", icon: Handshake, roles: ["admin"] },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, roles: ["admin"] },
  { to: "/services", label: "Service Categories", icon: Tag, roles: ["admin"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const isAdmin = user?.role === "admin";

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--eyb-canvas)" }}>
      {/* Sidebar */}
      <aside className="w-64 shrink-0 eyb-sidebar flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-5 flex items-center gap-3" style={{ borderBottom: "1px solid var(--eyb-hairline)" }}>
          <img src="/favicon.png" alt="EYB" className="w-9 h-9 shrink-0" />
          <div>
            <div className="font-heading text-[16px] font-semibold leading-tight" style={{ color: "var(--eyb-ink)", letterSpacing: "-0.02em" }}>EYB Consulting</div>
            <div className="eyb-label mt-1">CRM Portal</div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `eyb-nav-item ${isActive ? "is-active" : ""}`}
            >
              <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3" style={{ borderTop: "1px solid var(--eyb-hairline)" }}>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full text-white flex items-center justify-center text-sm font-semibold shrink-0"
                 style={{ background: "linear-gradient(135deg, #29ABE2 0%, #1B4F8B 100%)", boxShadow: "0 1px 2px rgba(10,37,64,0.15)" }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate" style={{ color: "var(--eyb-ink)" }}>{user?.name}</div>
              <div className="text-[11px] truncate capitalize" style={{ color: "var(--eyb-ink-3)" }}>{user?.role?.replace("_", " ")}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 eyb-topbar px-6 flex items-center justify-between">
          <div className="flex items-center gap-3" style={{ color: "var(--eyb-ink-4)" }}>
            <Search className="w-4 h-4" />
            <span className="text-[13px]">Quick search disabled — use page filters</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="header-notifications"
              className="p-2 rounded-lg transition-colors"
              style={{ color: "var(--eyb-ink-3)" }}
              title="Notifications"
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(10, 37, 64, 0.05)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <Bell className="w-4 h-4" strokeWidth={1.75} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-full transition-colors"
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(10, 37, 64, 0.04)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-semibold"
                       style={{ background: "linear-gradient(135deg, #29ABE2 0%, #1B4F8B 100%)" }}>
                    {initials}
                  </div>
                  <div className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-[13px] font-semibold max-w-[140px] truncate" style={{ color: "var(--eyb-ink)" }}>{user?.name}</span>
                    <span className="text-[10px] capitalize" style={{ color: "var(--eyb-ink-3)" }}>{user?.role?.replace("_", " ")}</span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5" style={{ color: "var(--eyb-ink-4)" }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60" data-testid="user-menu-content">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold truncate">{user?.name}</span>
                    <span className="text-[11px] font-normal truncate" style={{ color: "var(--eyb-ink-3)" }}>{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-profile"
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer"
                >
                  <UserIcon className="w-4 h-4 mr-2 text-slate-500" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="user-menu-change-password"
                  onClick={() => setShowChangePwd(true)}
                  className="cursor-pointer"
                >
                  <KeyRound className="w-4 h-4 mr-2 text-slate-500" />
                  Change Password
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem
                    data-testid="user-menu-settings"
                    onClick={() => navigate("/settings")}
                    className="cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-2 text-slate-500" />
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-logout"
                  onClick={doLogout}
                  className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto eyb-fade-in">
          <Outlet />
        </div>
      </main>

      <ChangePasswordModal open={showChangePwd} onClose={() => setShowChangePwd(false)} />
    </div>
  );
}
